import type { ReactNode } from 'react';
import { Container, Paper, Text } from '@/components/primitives';
import { vars } from '@/theme';

export interface ErrorScreenLayoutProps {
  /** Single character shown in the circular badge ('!', '?', '–'). */
  icon: string;
  /** Badge background colour. */
  iconBgColor: string;
  testId?: string;
  children: ReactNode;
}

/**
 * Shared full-page shell for error screens — a centered card with a circular
 * icon badge. Used by FailureScreen and NoticeScreen. Inline styles so it
 * renders even when the VE theme context is unavailable.
 */
export function ErrorScreenLayout({ icon, iconBgColor, testId, children }: ErrorScreenLayoutProps) {
  return (
    <div
      data-testid={testId}
      style={{
        // Fixed overlay so it covers the viewport whether it replaces a
        // crashed subtree (boundaries) or floats over a live app
        // (GlobalFailureScreen) — never a broken screen showing behind it.
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: vars.color.surface.muted,
        padding: 24,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          style={{
            padding: 40,
            textAlign: 'center',
            borderRadius: 0,
            border: '1px solid var(--color-grey-200)',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: iconBgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              marginBottom: 24,
            }}
          >
            <Text style={{ fontSize: 28, lineHeight: 1, fontWeight: 600 }}>{icon}</Text>
          </div>
          {children}
        </Paper>
      </Container>
    </div>
  );
}
